/**
 * World generation — the pipeline that ties the stages together.
 *
 * Order matters and is not arbitrary:
 *
 *   elevation → land mask → drown specks → water bodies → climate → terrain
 *   → features → depression fill → rivers → resources
 *
 * Climate must follow the land mask, because moisture is advected over water and
 * blocked by mountains. Rivers must follow climate, because sources are chosen from
 * wet highlands. Resources must follow terrain, because their placement rules are
 * expressed in terrain keys. Anything earlier in the chain cannot see anything later,
 * which is what keeps each stage independently testable.
 *
 * Every stage draws from the same named `mapgen` stream in a fixed order, so a seed
 * reproduces a world exactly.
 */

import type { Content } from '../../../content/schema/index.js';
import type { RngState } from '../rng/index.js';
import type { TileGrid } from '../state/types.js';
import { buildClimate, type ClimateField } from './climate.js';
import { buildElevation } from './elevation.js';
import {
  buildLandMask,
  classifyWaterBodies,
  fillDepressions,
  findRegions,
  openWaterStats,
  removeSmallIslands,
  traceRivers,
  type OpenWaterStats,
} from './hydrology.js';
import type { MapgenParams } from './params.js';
import { placeResources, type ResourceReport } from './resources.js';
import { assignFeatures, assignTerrain } from './terrain.js';

export interface WorldReport {
  landTiles: number;
  waterTiles: number;
  lakeCount: number;
  drownedIslandTiles: number;
  riverCount: number;
  riverTiles: number;
  resources: ResourceReport;
  /** Sizes of the connected landmasses, largest first. */
  continentSizes: number[];
  /** How evenly the land is spread — see openWaterStats. */
  openWater: OpenWaterStats;
}

export interface GeneratedWorld {
  report: WorldReport;
  /** Kept for tools and tests; not stored in the saved game. */
  climate: ClimateField;
}

/**
 * Generate a world into `grid` in place.
 *
 * Writes terrain, feature, resource, elevation and rivers. Leaves owner,
 * cultureOwner, workedBy and improvement untouched — those belong to civilisations,
 * which do not exist until Phase 3.
 */
export function generateWorld(
  grid: TileGrid,
  content: Content,
  params: MapgenParams,
  rng: RngState,
): GeneratedWorld {
  const { width, height, wrapX } = grid;

  // 1. Height field from plates, warped noise and boundary uplift.
  const elevation = buildElevation(width, height, wrapX, params, rng);

  // 2. Land, then remove specks too small to be worth anything.
  const isLand = buildLandMask(elevation.height, elevation.seaLevel);
  const drownedIslandTiles = removeSmallIslands(grid, isLand, params.minIslandSize);

  // 3. Ocean, coast and lakes, now that the land mask is final.
  const water = classifyWaterBodies(grid, isLand, params);

  // 4. Climate over the finished coastline.
  const climate = buildClimate(
    width,
    height,
    wrapX,
    elevation.height,
    elevation.seaLevel,
    isLand,
    params,
    rng,
  );

  // 5. Terrain, then features that depend on it.
  const relief = assignTerrain(
    grid,
    content,
    elevation.height,
    isLand,
    water.isLake,
    water.isCoast,
    climate,
    params,
  );
  assignFeatures(grid, content, isLand, climate, params, rng);

  // 6. Rivers, over a surface with its basins filled so every course reaches water.
  const filled = fillDepressions(grid, elevation.height, isLand);
  const rivers = traceRivers(grid, filled, isLand, climate.moisture, relief.hill, params, rng);
  grid.rivers.set(rivers.rivers);

  // 7. Resources last: their rules are expressed in terrain, so terrain must be final.
  const resources = placeResources(grid, content, isLand, params, rng);

  let landTiles = 0;
  for (let i = 0; i < isLand.length; i++) if (isLand[i] === 1) landTiles++;

  return {
    report: {
      landTiles,
      waterTiles: isLand.length - landTiles,
      lakeCount: water.lakeCount,
      drownedIslandTiles,
      riverCount: rivers.riverCount,
      riverTiles: rivers.riverTiles,
      resources,
      continentSizes: continentSizes(grid, isLand),
      openWater: openWaterStats(grid, isLand),
    },
    climate,
  };
}

/** Sizes of the connected landmasses, largest first. */
function continentSizes(grid: TileGrid, isLand: Uint8Array): number[] {
  return findRegions(grid, isLand, 1).sizes.sort((a, b) => b - a);
}
